import { getStatusConfig, type StatusModule } from '@client/utils/status/status-config';
import { type StatusRole, statusEntry } from '@client/utils/status/status-roles';
import { Tag } from 'antd';
import clsx from 'clsx';
import type { HTMLAttributes } from 'react';
import { Indicator } from './Indicator';

type StatusVariant = 'dot' | 'filled' | 'outlined';

interface StatusBadgeProps {
    /** The status value (e.g., 'open', 'closed') */
    status: string;
    /** The module this status belongs to */
    module: StatusModule;
    /** Display variant: dot (indicator only), filled (pill badge), outlined (Ant Design Tag) */
    variant?: StatusVariant;
    /** Custom label to display (overrides config label) */
    customLabel?: string;
    /** Additional CSS class name */
    className?: string;
    /** Override icon from config (useful for dynamic icons) */
    icon?: React.ReactNode;
    /** Override the configured role for a dynamic state (for example a stuck run needs `attention`) */
    role?: StatusRole;
}

/**
 * Event handlers and ARIA attributes an Ant Design `Tooltip`, `Popover` or `Dropdown` injects into
 * its trigger child are forwarded to the badge's root element, so a badge can be a trigger.
 */
type StatusBadgeRootProps = Omit<HTMLAttributes<HTMLElement>, 'className' | 'children' | 'role'>;

/**
 * Unified status badge component that supports multiple display variants:
 * - `dot`: Simple coloured indicator with tooltip
 * - `filled`: Pill-shaped badge with a role-coloured edge on the surface it is drawn on (for headers)
 * - `outlined`: Ant Design outlined Tag (for tables/lists)
 */
export function StatusBadge({
    status,
    module,
    variant = 'outlined',
    customLabel,
    className,
    icon: iconProp,
    role: roleProp,
    ...rest
}: StatusBadgeProps & StatusBadgeRootProps) {
    if (!status) return null;
    // A status the module does not define still renders, as a neutral badge with the raw value, so a
    // new enum member or a legacy row never makes the status disappear from the page.
    const baseConfig = getStatusConfig(module, status) ?? statusEntry('idle', status);

    // A role override re-derives every colour field, so all three variants follow it.
    const config = roleProp ? statusEntry(roleProp, baseConfig.label, baseConfig.icon) : baseConfig;
    const label = customLabel || config.label;

    // Resolve icon: prop takes precedence, then config
    const IconComponent = config.icon;
    const resolvedIcon =
        iconProp !== undefined ? iconProp : IconComponent ? <IconComponent className="h-3 w-3 flex-shrink-0" /> : null;

    // Dot variant - simple indicator with tooltip
    if (variant === 'dot') {
        return (
            <Indicator
                tooltip={{ title: label }}
                style={{ background: config.cssColor }}
                className={className}
                {...rest}
            />
        );
    }

    // Filled variant - pill-shaped badge (used in headers). The pill has no fill of its own: it takes
    // the surface it is drawn on, with `-foreground` text and a role-coloured edge. `check:contrast`
    // gates both (text 4.5:1, edge 3:1) on cards, the canvas, `bg-muted` and title bars in both
    // themes; a pale tinted fill lost its shape on a tinted header (1.18:1). A tone without an edge
    // colour (violet) keeps its own solid fill. With no icon configured a leading dot (in the text
    // colour) marks the pill as a status; an explicit `icon={null}` suppresses it.
    if (variant === 'filled') {
        return (
            <span
                className={clsx(
                    'inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-semibold text-sm',
                    config.edgeClass ? ['border', config.edgeClass] : config.bgClass,
                    config.textClass,
                    className,
                )}
                {...rest}
            >
                {iconProp === undefined && !IconComponent ? (
                    <span aria-hidden="true" className="size-2 flex-shrink-0 rounded-full bg-current" />
                ) : (
                    resolvedIcon
                )}
                {label}
            </span>
        );
    }

    // Outlined variant - Ant Design Tag (default, used in tables)
    // Render icon as child with flex alignment instead of using icon prop for better vertical centering
    return (
        <Tag
            variant="outlined"
            color={config.color}
            className={clsx('inline-flex items-center gap-1', className)}
            {...rest}
        >
            {resolvedIcon}
            {label}
        </Tag>
    );
}
